import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';

export interface LcPoolSummary {
  poolId: string;
  name: string;
  description: string | null;
  tokenA: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  tokenB: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  reserveA: string;
  reserveB: string;
  totalLpShares: string;
  feeBps: number;
  minLiquidity: number;
  maxTradePct: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface LcPoolListResult {
  pools: LcPoolSummary[];
  total: number;
  limit: number;
  offset: number;
}

interface UseLcPoolsReturn {
  pools: LcPoolSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useLiquidityCachePools(): UseLcPoolsReturn {
  const [pools, setPools] = useState<LcPoolSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchPools = useCallback(async (newOffset: number = 0, append: boolean = false) => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result: LcPoolListResult = await api.listLiquidityCachePools({
        limit,
        offset: newOffset,
      });
      if (fetchIdRef.current !== id) return;
      if (append) {
        setPools((prev) => [...prev, ...result.pools]);
      } else {
        setPools(result.pools);
      }
      setTotal(result.total);
      setOffset(newOffset);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load pools');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchPools(0, false);
  }, [fetchPools]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchPools(offset + limit, true);
    }
  }, [fetchPools, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    pools,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
